import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type Theme = 'light' | 'dark' | 'system'

export interface ThemeState {
  theme: Theme

  // Actions
  setTheme: (theme: Theme) => void
  toggleTheme: () => void
}

// Helper function to get system theme
const getSystemTheme = (): 'light' | 'dark' => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  return 'dark'
}

// Helper function to get effective theme
const getEffectiveTheme = (theme: Theme): 'light' | 'dark' => {
  return theme === 'system' ? getSystemTheme() : theme
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: 'system', // Default to system preference

      setTheme: (theme: Theme) => {
        set({ theme })

        // Apply theme to document
        const root = document.documentElement
        const effectiveTheme = getEffectiveTheme(theme)
        root.classList.remove('light', 'dark')
        root.classList.add(effectiveTheme)

        // Listen for system theme changes if theme is 'system'
        if (theme === 'system' && typeof window !== 'undefined') {
          const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
          const handleChange = (e: MediaQueryListEvent) => {
            const newEffectiveTheme = e.matches ? 'dark' : 'light'
            root.classList.remove('light', 'dark')
            root.classList.add(newEffectiveTheme)
          }

          // Remove existing listener
          mediaQuery.removeEventListener('change', handleChange)
          // Add new listener
          mediaQuery.addEventListener('change', handleChange)
        }
      },

      toggleTheme: () => {
        const currentTheme = get().theme
        // Simple toggle between light and dark (skip system)
        const newTheme = currentTheme === 'light' ? 'dark' : 'light'
        get().setTheme(newTheme)
      },
    }),
    {
      name: 'theme-store',
      onRehydrateStorage: () => state => {
        // Apply theme immediately when store rehydrates
        if (state?.theme) {
          const root = document.documentElement
          const effectiveTheme = getEffectiveTheme(state.theme)
          root.classList.remove('light', 'dark')
          root.classList.add(effectiveTheme)

          // Set up system theme listener if needed
          if (state.theme === 'system' && typeof window !== 'undefined') {
            const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
            const handleChange = (e: MediaQueryListEvent) => {
              const newEffectiveTheme = e.matches ? 'dark' : 'light'
              root.classList.remove('light', 'dark')
              root.classList.add(newEffectiveTheme)
            }
            mediaQuery.addEventListener('change', handleChange)
          }
        }
      },
    }
  )
)
